import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import API from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/lib/AuthContext";
import { ArrowLeft, Send, Loader2, Trash2, Flag, Shield, Ban, Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ForumAvatar from "@/components/ForumAvatar";
import { useToast } from "@/hooks/use-toast";

function PostCard({ post, onDelete, onFlag, onBan, onBlock, isAdmin, currentUserId }) {
  const [flagging, setFlagging] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const isOwnPost = post.author_app_user_id && currentUserId && post.author_app_user_id === currentUserId;
  const canBlock = !isAdmin && !isOwnPost && post.author_app_user_id && !post.author_is_provider;

  return (
    <div className={`flex gap-3 ${post.deleted ? "opacity-60" : ""}`} data-testid={`forum-post-${post.id}`}>
      <ForumAvatar post={post} size="md" />
      <div className="flex-1 min-w-0 bg-card border border-border rounded-2xl p-3.5">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-medium">{post.author_display_name}</span>
          {post.author_is_provider && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
              <Shield className="w-2.5 h-2.5" /> Care Team
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            · {formatDistanceToNow(new Date(post.created_date), { addSuffix: true })}
          </span>
        </div>
        {post.title && <h4 className="font-semibold mb-1.5">{post.title}</h4>}
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{post.body}</p>
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          {!post.deleted && !flagging && (
            <button
              onClick={() => setFlagging(true)}
              className="inline-flex items-center gap-1 hover:text-amber-600 transition-colors"
              data-testid={`forum-flag-button-${post.id}`}
            >
              <Flag className="w-3 h-3" /> Report
            </button>
          )}
          {isAdmin && !post.deleted && (
            <>
              <button
                onClick={() => onDelete(post)}
                className="inline-flex items-center gap-1 hover:text-red-600 transition-colors"
                data-testid={`forum-admin-delete-${post.id}`}
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
              <button
                onClick={() => onBan(post)}
                className="inline-flex items-center gap-1 hover:text-red-600 transition-colors"
                data-testid={`forum-admin-ban-${post.id}`}
              >
                <Ban className="w-3 h-3" /> Ban user
              </button>
            </>
          )}
          {canBlock && (
            <button
              onClick={() => onBlock(post)}
              className="inline-flex items-center gap-1 hover:text-red-600 transition-colors"
              data-testid={`forum-block-button-${post.id}`}
            >
              <Ban className="w-3 h-3" /> Block
            </button>
          )}
          {(post.flags?.length || 0) > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-600 ml-auto" data-testid={`forum-flag-count-${post.id}`}>
              <Flag className="w-3 h-3" /> {post.flags.length}
            </span>
          )}
        </div>
        {flagging && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid={`forum-flag-form-${post.id}`}>
            <p className="text-xs font-semibold text-amber-900">Report this post</p>
            <Textarea
              rows={2}
              placeholder="Optional: why does this violate community safety?"
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              className="text-xs"
            />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => { setFlagging(false); setFlagReason(""); }}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                onClick={async () => {
                  await onFlag(post, flagReason);
                  setFlagging(false);
                  setFlagReason("");
                }}
                data-testid={`forum-flag-submit-${post.id}`}
              >
                Submit report
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ForumThread() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [blockTarget, setBlockTarget] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [muting, setMuting] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await API.get(`/forum/threads/${threadId}`);
      setThread(data.thread);
      setReplies(data.replies || []);
      setIsMuted(!!data.is_muted);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: "Could not load thread", description: typeof detail === "string" ? detail : "", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [threadId, toast]);
  useEffect(() => { load(); }, [load]);

  // Clear forum_reply notifications when opening a thread
  useEffect(() => {
    API.post("/notifications/mark-read-by-type", { type: "forum_reply" })
      .then(() => window.dispatchEvent(new CustomEvent("notifications:refresh")))
      .catch(() => {});
  }, [threadId]);

  const sendReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await API.post(`/forum/threads/${threadId}/replies`, { body: reply.trim() });
      setReply("");
      await load();
    } catch (err) {
      toast({ title: "Couldn't reply", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const isThread = deleteTarget.id === thread?.id;
      await API.delete(`/forum/${isThread ? "threads" : "replies"}/${deleteTarget.id}`);
      toast({ title: isThread ? "Thread removed" : "Reply removed" });
      setDeleteTarget(null);
      if (isThread) navigate("/forum");
      else load();
    } catch {
      toast({ title: "Couldn't delete", variant: "destructive" });
    }
  };

  const handleFlag = async (post, reason) => {
    try {
      const isThread = post.id === thread?.id;
      await API.post(`/forum/${isThread ? "threads" : "replies"}/${post.id}/flag`, { reason });
      toast({ title: "Report submitted", description: "Thanks — the care team will review." });
      load();
    } catch {
      toast({ title: "Couldn't submit report", variant: "destructive" });
    }
  };

  const handleBan = async () => {
    if (!banTarget) return;
    try {
      await API.post(`/forum/users/${banTarget.author_app_user_id}/ban`, { reason: banReason });
      toast({ title: `${banTarget.author_display_name} banned from the forum` });
      setBanTarget(null);
      setBanReason("");
      load();
    } catch (err) {
      toast({ title: "Couldn't ban", description: err?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const handleBlock = async () => {
    if (!blockTarget) return;
    try {
      await API.post(`/forum/block-user`, { target_user_id: blockTarget.author_app_user_id });
      toast({ title: "User blocked", description: `You won't see posts or replies from ${blockTarget.author_display_name} anymore.` });
      setBlockTarget(null);
      // If we just blocked the thread author, bounce back to the forum index
      if (blockTarget.id === thread?.id) navigate("/forum");
      else load();
    } catch (err) {
      toast({ title: "Couldn't block", description: err?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const toggleMute = async () => {
    if (muting) return;
    setMuting(true);
    const next = !isMuted;
    try {
      await API.post(`/forum/threads/${threadId}/${next ? "mute" : "unmute"}`);
      setIsMuted(next);
      toast({
        title: next ? "Notifications paused" : "Notifications resumed",
        description: next
          ? "You won't get pings for new replies on this thread."
          : "You'll be notified again when someone replies.",
      });
    } catch (err) {
      toast({ title: "Couldn't update notifications", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setMuting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  if (!thread) return <div className="text-center py-20 text-sm text-muted-foreground">Thread not found.</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-4 animate-fade-in" data-testid="forum-thread-page">
      <div className="flex items-center justify-between gap-2">
        <Link to="/forum" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="forum-thread-back">
          <ArrowLeft className="w-4 h-4" /> Back to forum
        </Link>
        <button
          type="button"
          onClick={toggleMute}
          disabled={muting}
          aria-pressed={isMuted}
          data-testid="forum-thread-mute-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            isMuted
              ? "bg-muted border-border text-muted-foreground"
              : "bg-card border-border text-foreground hover:bg-accent"
          }`}
          title={isMuted ? "Notifications paused for this thread" : "Pause notifications for this thread"}
        >
          {isMuted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
          {isMuted ? "Muted" : "Mute"}
        </button>
      </div>

      <PostCard
        post={thread}
        isAdmin={isAdmin}
        currentUserId={user?.id}
        onDelete={setDeleteTarget}
        onFlag={handleFlag}
        onBan={(p) => setBanTarget(p)}
        onBlock={(p) => setBlockTarget(p)}
      />

      <div className="border-l-2 border-border ml-5 pl-4 space-y-3" data-testid="forum-replies-list">
        {replies.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-2">No replies yet. Be the first to respond.</p>
        ) : replies.map((r) => (
          <PostCard
            key={r.id}
            post={r}
            isAdmin={isAdmin}
            currentUserId={user?.id}
            onDelete={setDeleteTarget}
            onFlag={handleFlag}
            onBan={(p) => setBanTarget(p)}
            onBlock={(p) => setBlockTarget(p)}
          />
        ))}
      </div>

      {!thread.deleted && (
        <div className="bg-card border border-border rounded-2xl p-3.5 sticky bottom-4" data-testid="forum-reply-form">
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            maxLength={5000}
            placeholder="Share your reply… (share at your own risk)"
            className="resize-none"
            data-testid="forum-reply-input"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-muted-foreground">{reply.length}/5000</span>
            <Button size="sm" className="rounded-full gap-1" onClick={sendReply} disabled={sending || !reply.trim()} data-testid="forum-reply-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4" /> Reply</>)}
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="forum-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently remove this {deleteTarget?.id === thread?.id ? "thread" : "reply"}?</AlertDialogTitle>
            <AlertDialogDescription>This is permanent. The post will be erased from the forum — members will see no trace of it or its replies.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!banTarget} onOpenChange={(o) => !o && (setBanTarget(null), setBanReason(""))}>
        <AlertDialogContent data-testid="forum-ban-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ban {banTarget?.author_display_name} from the forum?</AlertDialogTitle>
            <AlertDialogDescription>
              They won't be able to view or post in the ADHD Club Forum until you unban them. Their existing posts will remain unless you also delete them. This does not affect their patient portal access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1">
            <Textarea
              rows={3}
              placeholder="Optional internal note for the ban…"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              data-testid="forum-ban-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBan} className="bg-red-600 hover:bg-red-700" data-testid="forum-ban-confirm">Ban user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!blockTarget} onOpenChange={(o) => !o && setBlockTarget(null)}>
        <AlertDialogContent data-testid="forum-block-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Block {blockTarget?.author_display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              You won't see their threads or replies in the forum anymore. They won't be notified, and this doesn't affect the rest of the practice. You can undo this later from your profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBlock} className="bg-red-600 hover:bg-red-700" data-testid="forum-block-confirm">Block user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
